/**
 * AI API types for mstream embedded agent.
 *
 * ContextItem mirrors mstream's tagged enum (serde: { tag: "kind", rename_all: "snake_case" }).
 * The FE builds ContextItem[] from local Studio state and sends it with each chat request.
 */

// ---------------------------------------------------------------------------
// Context items — matches mstream ContextItem enum
// ---------------------------------------------------------------------------

export interface NamedScript {
  name: string;
  content: string;
  service_name?: string;
}

export interface ContextTestResult {
  passed: boolean;
  actual?: unknown;
  error?: string;
}

export interface ContextTestCase {
  name: string;
  input: unknown;
  expected?: unknown;
  assertions: unknown[];
  last_result?: ContextTestResult;
}

export type ContextItem =
  | {
      kind: "input";
      content: unknown;
      service_name?: string;
      resource?: string;
    }
  | { kind: "scripts"; content: NamedScript[] }
  | {
      kind: "schema";
      content: unknown;
      schema_id?: string;
      service_name?: string;
    }
  | { kind: "output"; content: unknown }
  | { kind: "test_cases"; content: ContextTestCase[]; suite_id?: string }
  | { kind: "error"; content: string }
  | {
      kind: "workspace_info";
      content: {
        id: string | null;
        name: string | null;
        saved: boolean;
        linked_scripts: number;
        has_schema: boolean;
        has_test_suite: boolean;
      };
    }
  | {
      kind: "pipeline";
      content: {
        name: string;
        source: {
          service_name: string;
          resource: string;
          output_encoding: string;
        } | null;
        middlewares: {
          service_name: string;
          resource: string;
          output_encoding: string;
        }[];
        sinks: {
          service_name: string;
          resource: string;
          output_encoding: string;
        }[];
        checkpoint: { enabled: boolean; interval_seconds?: number } | null;
        batch: { kind: string; size: number } | null;
      };
      unresolved_services?: string[];
    };

export type ConversationMode = "ask" | "act";

export type ConversationContext = "studio" | "agent";

export type ToolConfirmStatus =
  | "executing"
  | "complete"
  | "error"
  | "pending"
  | "denied";

export interface ToolCallEntry {
  id: string;
  name: string;
  status: ToolConfirmStatus;
  arguments?: unknown;
  result?: string;
}

// ---------------------------------------------------------------------------
// Chat request / response — matches mstream POST /ai/chat
// ---------------------------------------------------------------------------

export interface ChatRequest {
  message: string;
  conversation_id?: string;
  service_name?: string;
  allow_writes: boolean;
  mode?: ConversationMode;
  context_id?: ConversationContext;
  context: ContextItem[];
  system_prompt?: string;
}

export interface ChatResponse {
  conversation_id: string;
  response: string;
  tool_rounds: number;
}

// ---------------------------------------------------------------------------
// Conversation — matches mstream GET /ai/conversations
// ---------------------------------------------------------------------------

export interface ConversationSummary {
  id: string;
  title: string;
  mode?: ConversationMode;
  context?: ConversationContext;
  created_at: string;
  updated_at: string;
}

export interface ConversationMessage {
  role: "user" | "assistant" | "tool";
  content: string;
  tool_call_id?: string;
  tool_calls?: ToolCall[];
  timestamp: string;
}

export interface ToolCall {
  id: string;
  name: string;
  arguments: unknown;
}

export interface Conversation {
  id: string;
  user_id: string;
  title: string;
  mode?: ConversationMode;
  context?: ConversationContext;
  messages: ConversationMessage[];
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// UI message model — used by AiChatContext and components
// ---------------------------------------------------------------------------

export enum MessageType {
  User = "user",
  Assistant = "assistant",
}

export enum MessageStatus {
  Pending = "pending",
  Streaming = "streaming",
  Complete = "complete",
  Error = "error",
}

export interface DisplayMessage {
  messageId: string;
  message: string;
  type: MessageType;
  timestamp: number;
  status: MessageStatus;
  context?: ContextItem[];
  toolRounds?: number;
  toolCalls?: ToolCallEntry[];
}

// ---------------------------------------------------------------------------
// SSE Stream events — matches mstream StreamEvent enum
// ---------------------------------------------------------------------------

export type StreamEvent =
  | { type: "init"; conversation_id: string }
  | { type: "text"; content: string }
  | { type: "tool_call"; id: string; name: string }
  | { type: "tool_result"; id: string; content: string }
  | { type: "tool_confirm"; id: string; name: string; arguments: unknown }
  | { type: "error"; message: string }
  | { type: "done" };
